// Libs
import classNames from 'classnames/bind';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import { TypingDots, UnderDevelopment } from '~/components/common';
import {
  ConversationUserInfo,
  ConversationInputMessage,
  ConversationMessageContent,
} from '~/components/module/conversation';
// Others
import { IMessage, ISubmitMessage } from '~/utils/interface/message';
import { useAppDispatch } from '~/utils/redux/hooks';
import { createNewMessage, getListMessage } from '~/thunks/conversation/conversationThunk';
import { StorageEnum } from '~/utils/enum/base';
import {
  IBodyCreateNewMessage,
  IMemberMessage,
  IQueryCreateNewMessage,
  IQueryGetListMessage,
} from '~/utils/interface/conversation';
import { generateUniqueId } from '~/utils/helper/common';
import { WebSocketContext } from '~/context';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { conversationActions } from '~/thunks/conversation/conversationSlice';
import { MessageStatusEnum } from '~/utils/enum/common';
import { IProfile } from '~/utils/interface/user';
import { selectProfile } from '~/thunks/user/userSlice';
import {
  joinConversation,
  leaveConversation,
  seenMessage,
  stopTyping,
  typing,
} from '~/utils/helper/websocket';
import useVisibleMessages from '~/utils/hooks/useVisibleMessages';
import { WebSocketEventEnum } from '~/utils/enum/websocket';
// Styles, images, icons
import styles from './Conversation.module.scss';

type Props = {
  conversationId?: string;
  onRefreshConversation: () => void;
};

const cx = classNames.bind(styles);

const Conversation = (props: Props) => {
  //#region Destructuring Props
  const { conversationId, onRefreshConversation } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/message']);
  const dispatch = useAppDispatch();
  const accountId = localStorage.getItem(StorageEnum.ACCOUNT_ID);
  const { message: messageWSS, wss, socketId } = useContext(WebSocketContext);
  const messageContentRef = useRef<HTMLDivElement | null>(null);
  //#endregion Declare Hook

  //#region Selector
  const accountInfo: IProfile = useSelector(selectProfile);
  //#endregion Selector

  //#region Declare State
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [isOpenModalUnderDevelop, setIsOpenModalUnderDevelop] = useState<boolean>(false);
  const [isLoadingSendMessage, setIsLoadingSendMessage] = useState<boolean>(false);
  const [isLoadMore, setIsLoadMore] = useState<boolean>(false);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [members, setMembers] = useState<IMemberMessage[]>([]);
  const [currentPageMessage, setCurrentPageMessage] = useState<number>(BASE_PAGINATION_DEFAULT_CURRENT_PAGE);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const visibleMessages = useVisibleMessages(messages, containerRef);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(true);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (!conversationId) return;

    const params: IQueryGetListMessage = {
      conversationId,
      params: {
        page: currentPageMessage,
        limit: DEFAULT_LIMIT_PAGE,
      },
    };

    handleGetListMessage(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, currentPageMessage]);

  useEffect(() => {
    if (Array.isArray(visibleMessages) && visibleMessages.length === 0) {
      return;
    }

    if (!wss || !conversationId) {
      return;
    }

    seenMessage(wss, +conversationId, visibleMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleMessages]);

  useEffect(() => {
    if (!conversationId) {
      return;
    }
    if (wss) {
      joinConversation(wss, +conversationId);
    }

    return () => {
      if (wss) {
        leaveConversation(wss, +conversationId);
      }
    };
  }, [wss, conversationId]);

  useEffect(() => {
    if (!messageWSS) {
      return;
    }

    switch (messageWSS.type) {
      case WebSocketEventEnum.GET_MESSAGE_UNSEEN:
        break;
      case WebSocketEventEnum.SEEN_MESSAGE:
        const messageIdsUpdated = messageWSS?.data?.messageIdsUpdated;
        if (String(messageWSS?.data?.conversationId) === String(conversationId) && messageIdsUpdated) {
          const newListMessages = messages.map((message) => {
            if (messageIdsUpdated.includes(Number(message.id))) {
              return {
                ...message,
                status: MessageStatusEnum.SEEN,
              };
            }
            return message;
          });

          setMessages(newListMessages);
        }
        break;
      case WebSocketEventEnum.SEND_MESSAGE:
        if (String(messageWSS?.data?.conversationId) === String(conversationId)) {
          const newMessage: IMessage = {
            id: messageWSS.data.id,
            content: messageWSS.data.content,
            type: messageWSS.data.type,
            status: MessageStatusEnum.DELIVERED,
            createdAt: messageWSS.data.createdAt,
            senderAccountId: messageWSS.data.senderAccountId,
            senderAvatarImage: members?.[DEFAULT_NUMBER_ZERO]?.avatarImage,
            senderFirstName: members?.[DEFAULT_NUMBER_ZERO]?.firstName,
            senderLastName: members?.[DEFAULT_NUMBER_ZERO]?.lastName,
            files: messageWSS.data.files,
          };
          setMessages((prev) => [newMessage, ...prev]);
        }
        break;
      case WebSocketEventEnum.TYPING:
        if (String(messageWSS?.data?.conversationId) === String(conversationId)) {
          setIsTyping(true);
        }
        break;
      case WebSocketEventEnum.STOP_TYPING:
        if (String(messageWSS?.data?.conversationId) === String(conversationId)) {
          setIsTyping(false);
        }
        break;
      case WebSocketEventEnum.ONLINE:
        setMembers((prevState) =>
          prevState.map((member) =>
            member.id === messageWSS?.data?.id?.toString() ? { ...member, isOnline: true } : member
          )
        );
        break;
      case WebSocketEventEnum.OFFLINE:
        setMembers((prevState) =>
          prevState.map((member) =>
            member.id === messageWSS?.data?.id?.toString() ? { ...member, isOnline: false } : member
          )
        );
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageWSS]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListMessage = (params: IQueryGetListMessage) => {
    if (!params) return;

    setIsLoadingSendMessage(true);

    dispatch(getListMessage(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { members: listMember, messages: listMessage, pagination } = res?.data;

        if (!members || !listMessage) return;

        if (params.conversationId !== conversationId) return;

        setMembers(listMember);
        setHasMoreMessages(pagination?.page < pagination?.totalPages);

        if (isLoadMore) {
          setMessages((prevState) => [...prevState, ...listMessage]);
          return;
        }
        setMessages(listMessage);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingSendMessage(false);
        setIsLoadMore(false);
      });
  };

  const handleFocus = () => {
    if (!wss || !conversationId) {
      return;
    }
    typing(wss, +conversationId);
  };

  const handleBlur = () => {
    if (!wss || !conversationId) {
      return;
    }
    stopTyping(wss, +conversationId);
  };

  const handleSendMessage = async (data: ISubmitMessage) => {
    const { type, content, files } = data;

    if (!conversationId || !accountId) return;

    const tempMessageId = generateUniqueId();
    const newMessage: IMessage = {
      id: tempMessageId,
      content: content || EMPTY_STRING,
      type,
      status: MessageStatusEnum.DELIVERED,
      createdAt: new Date(),
      senderAccountId: accountId,
      senderAvatarImage: accountInfo?.avatarImage,
      senderFirstName: accountInfo?.firstName,
      senderLastName: accountInfo?.lastName,
    };

    const newMessages = [newMessage, ...messages];
    setMessages(newMessages);

    const formDataConverted: IBodyCreateNewMessage = {
      tempId: tempMessageId,
      type,
      content,
      files,
      socketId: socketId as string,
    };

    if (messageContentRef.current) {
      messageContentRef.current.scrollTop = messageContentRef.current.scrollHeight;
    }

    const formData = new FormData();

    Object.entries(formDataConverted).forEach(([key, value]) => {
      if (value && key !== 'files') {
        formData.append(key, value);
      }
    });

    if (files && files.length > DEFAULT_NUMBER_ZERO) {
      files?.forEach((file) => {
        file && formData.append('files', file);
      });

      dispatch(
        conversationActions.setTempIdFile({
          tempIdSendFile: newMessage.id,
          typeFileLoading: files?.[DEFAULT_NUMBER_ZERO].type,
        })
      );
    }

    const payload: IQueryCreateNewMessage = {
      conversationId,
      formData,
    };

    setIsLoadingSendMessage(true);

    dispatch(createNewMessage(payload))
      .unwrap()
      .then((res) => {
        const data = res?.data;
        if (Number(data?.conversationId) !== Number(conversationId)) return;

        setMessages(
          newMessages.map((message) =>
            message.id === data?.tempId
              ? {
                  ...message,
                  id: data?.id,
                  files: data.files,
                  content: data.content || EMPTY_STRING,
                  status: data.status,
                  type: data.type,
                }
              : message
          )
        );

        onRefreshConversation();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingSendMessage(false);
      });
  };

  const handleCloseModalUnderDevelop = () => {
    setIsOpenModalUnderDevelop(!isOpenModalUnderDevelop);
  };

  const handleLoadMoreMessage = () => {
    if (isLoadingSendMessage || !hasMoreMessages || isLoadMore) return;

    setIsLoadMore(true);
    setCurrentPageMessage((prevState) => prevState + DEFAULT_NUMBER_ONE);
  };
  //#endregion Handle Function

  return (
    <Box id='conversation' className={cx('container')}>
      {conversationId ? (
        <>
          {members && members?.length > DEFAULT_NUMBER_ZERO && (
            <Box component={'div'} className={cx('conversationHeader')}>
              <ConversationUserInfo data={members?.[DEFAULT_NUMBER_ZERO]} />
            </Box>
          )}

          {conversationId && (
            <Box component={'div'} className={cx('conversationBody')}>
              <ConversationMessageContent
                data={messages || []}
                hasMoreMessages={hasMoreMessages}
                onLoadMoreMessage={handleLoadMoreMessage}
                containerRef={containerRef}
              />
            </Box>
          )}

          {members && members?.length > DEFAULT_NUMBER_ZERO && (
            <Box component={'div'} className={cx('conversationInputMessage')}>
              <Box component={'p'} className={cx('conversationUserTyping')}>
                {isTyping && <TypingDots />}
              </Box>

              <ConversationInputMessage
                onSubmit={handleSendMessage}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </Box>
          )}
        </>
      ) : (
        <Typography className={cx('textNoConversation')}>{t('conversation_text_no_conversation')}</Typography>
      )}

      {isOpenModalUnderDevelop && (
        <UnderDevelopment isOpen={isOpenModalUnderDevelop} onClose={handleCloseModalUnderDevelop} />
      )}
    </Box>
  );
};

export default Conversation;
